import {
  ComplianceDocumentChecklistItem_Read,
  ComplianceDocumentType_Read_Nested,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';

import {
  ComplianceDocumentType,
  ComplianceDocumentTypeBasic,
} from '~hooks/useComplianceDocumentTypes/models';

import { ComplianceDocument } from './models';

export type DriverContext = 'internal' | 'shared';

export interface StagedComplianceDoc {
  documentTypeId: string;
  value?: string;
  expiresAt?: string;
  file?: File;
}

interface ChecklistFileAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
}

export interface ChecklistItem {
  documentType: ComplianceDocumentTypeBasic | ComplianceDocumentType;
  document: ComplianceDocument | null;
  fileAttachment: ChecklistFileAttachment | null;
  stagedDoc?: StagedComplianceDoc;
  status: 'valid' | 'expiring' | 'expired' | 'missing' | 'optional';
}

/** Documents expiring within this many days are shown with a warning status */
export const EXPIRATION_WARNING_DAYS = 30;

export function parseChecklistItem(
  raw: ComplianceDocumentChecklistItem_Read,
): ChecklistItem {
  const documentType = ComplianceDocumentTypeBasic.parse(
    raw?.compliance_document_type ?? ({} as ComplianceDocumentType_Read_Nested),
  );
  const document = raw.compliance_document
    ? ComplianceDocument.parse(raw.compliance_document)
    : null;

  const fileAttachment = raw.file_attachment
    ? {
        id: raw.file_attachment.id,
        fileName: raw.file_attachment.file_name,
        fileUrl: raw.file_attachment.file_url,
      }
    : null;

  let status: ChecklistItem['status'] = 'missing';
  if (document) {
    if (!documentType.hasExpiration || !document.expiresAt) {
      status = 'valid';
    } else if (document.expiresAt.isBefore(dayjs())) {
      status = 'expired';
    } else if (document.expiresAt.isBefore(dayjs().add(EXPIRATION_WARNING_DAYS, 'day'))) {
      status = 'expiring';
    } else {
      status = 'valid';
    }
  } else if (!documentType.dispatchGating) {
    status = 'optional';
  }

  return { documentType, document, fileAttachment, status };
}
